import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { LearningTopic, StatsData } from '@/services/studentProgressService';
import {
    LearningJourneyData,
    ILearningTopicsGetListQuery,
    TodaySuggestion,
    IDailyProgress,
    IWeeklyProgress,
} from '@/features/student/StudentProgress/types';

interface StudentProgressState {
    learningJourney: LearningJourneyData | null;
    learningTopics?: LearningTopic;
    exploreTopics?: LearningTopic;
    todaySuggestions: TodaySuggestion[];
    dailyProgress: IDailyProgress[];
    weeklyProgress: IWeeklyProgress[];
    stats: StatsData | null;
    isLoading: {
        learningJourney: boolean;
        learningTopics: boolean;
        exploreTopics: boolean;
        todaySuggestions: boolean;
        stats: boolean;
        dailyProgress: boolean;
        weeklyProgress: boolean;
    };
    error: {
        learningJourney: string | null;
        learningTopics: string | null;
        exploreTopics: string | null;
        todaySuggestions: string | null;
        stats: string | null;
        dailyProgress: string | null;
        weeklyProgress: string | null;
    };
}

const initialState: StudentProgressState = {
    learningJourney: null,
    learningTopics: undefined,
    exploreTopics: undefined,
    todaySuggestions: [],
    dailyProgress: [],
    weeklyProgress: [],
    stats: null,
    isLoading: {
        learningJourney: false,
        learningTopics: false,
        exploreTopics: false,
        todaySuggestions: false,
        stats: false,
        dailyProgress: false,
        weeklyProgress: false,
    },
    error: {
        learningJourney: null,
        learningTopics: null,
        exploreTopics: null,
        todaySuggestions: null,
        stats: null,
        dailyProgress: null,
        weeklyProgress: null,
    },
};

const studentProgressSlice = createSlice({
    name: 'studentProgress',
    initialState,
    reducers: {
        // Learning Journey
        fetchLearningJourneyStart: (state) => {
            state.isLoading.learningJourney = true;
            state.error.learningJourney = null;
        },
        fetchLearningJourneySuccess: (state, action: PayloadAction<LearningJourneyData>) => {
            state.isLoading.learningJourney = false;
            state.learningJourney = action.payload;
        },
        fetchLearningJourneyFailure: (state, action: PayloadAction<string>) => {
            state.isLoading.learningJourney = false;
            state.error.learningJourney = action.payload;
        },

        // Learning Topics
        fetchLearningTopicsStart: (state, action: PayloadAction<ILearningTopicsGetListQuery>) => {
            state.isLoading.learningTopics = true;
            state.error.learningTopics = null;
        },
        fetchLearningTopicsSuccess: (state, action: PayloadAction<LearningTopic>) => {
            state.isLoading.learningTopics = false;
            state.learningTopics = action.payload;
        },
        fetchLearningTopicsFailure: (state, action: PayloadAction<string>) => {
            state.isLoading.learningTopics = false;
            state.error.learningTopics = action.payload;
        },
        // Explore Topics
        fetchExploreTopicsStart: (state, action: PayloadAction<ILearningTopicsGetListQuery>) => {
            state.isLoading.exploreTopics = true;
            state.error.exploreTopics = null;
        },
        fetchExploreTopicsSuccess: (state, action: PayloadAction<LearningTopic>) => {
            state.isLoading.exploreTopics = false;
            state.exploreTopics = action.payload;
        },
        fetchExploreTopicsFailure: (state, action: PayloadAction<string>) => {
            state.isLoading.exploreTopics = false;
            state.error.exploreTopics = action.payload;
        },
        // Today Suggestions
        fetchTodaySuggestionsStart: (state) => {
            state.isLoading.todaySuggestions = true;
            state.error.todaySuggestions = null;
        },
        fetchTodaySuggestionsSuccess: (state, action: PayloadAction<TodaySuggestion[]>) => {
            state.isLoading.todaySuggestions = false;
            state.todaySuggestions = action.payload;
        },
        fetchTodaySuggestionsFailure: (state, action: PayloadAction<string>) => {
            state.isLoading.todaySuggestions = false;
            state.error.todaySuggestions = action.payload;
        },
        // Daily Progress
        fetchDailyProgressStart: (state) => {
            state.isLoading.dailyProgress = true;
            state.error.dailyProgress = null;
        },
        fetchDailyProgressSuccess: (state, action: PayloadAction<IDailyProgress[]>) => {
            state.isLoading.dailyProgress = false;
            state.error.dailyProgress = null;
            state.dailyProgress = action.payload;
        },
        fetchDailyProgressFailure: (state, action: PayloadAction<string>) => {
            state.isLoading.dailyProgress = false;
            state.error.dailyProgress = action.payload;
        },
        // Weekly Progress
        fetchWeeklyProgressStart: (state) => {
            state.isLoading.weeklyProgress = true;
            state.error.weeklyProgress = null;
        },
        fetchWeeklyProgressSuccess: (state, action: PayloadAction<IWeeklyProgress[]>) => {
            state.isLoading.weeklyProgress = false;
            state.error.weeklyProgress = null;
            state.weeklyProgress = action.payload;
        },
        fetchWeeklyProgressFailure: (state, action: PayloadAction<string>) => {
            state.isLoading.weeklyProgress = false;
            state.error.weeklyProgress = action.payload;
        },
        // Stats
        fetchStatsStart: (state) => {
            state.isLoading.stats = true;
            state.error.stats = null;
        },
        fetchStatsSuccess: (state, action: PayloadAction<StatsData>) => {
            state.isLoading.stats = false;
            state.stats = action.payload;
        },
        fetchStatsFailure: (state, action: PayloadAction<string>) => {
            state.isLoading.stats = false;
            state.error.stats = action.payload;
        },
    },
});

export const {
    fetchLearningJourneyStart,
    fetchLearningJourneySuccess,
    fetchLearningJourneyFailure,
    fetchLearningTopicsStart,
    fetchLearningTopicsSuccess,
    fetchLearningTopicsFailure,
    fetchExploreTopicsStart,
    fetchExploreTopicsSuccess,
    fetchExploreTopicsFailure,
    fetchTodaySuggestionsStart,
    fetchTodaySuggestionsSuccess,
    fetchTodaySuggestionsFailure,
    fetchDailyProgressStart,
    fetchDailyProgressSuccess,
    fetchDailyProgressFailure,
    fetchWeeklyProgressStart,
    fetchWeeklyProgressSuccess,
    fetchWeeklyProgressFailure,
    fetchStatsStart,
    fetchStatsSuccess,
    fetchStatsFailure,
} = studentProgressSlice.actions;

export default studentProgressSlice.reducer;
